import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	tableCellClasses,
} from '@mui/material'
import { TypographySize } from 'components/cattles/checkupPage/processes/typography'
import {
	EMPTY_HISTORY_CELL,
	buildCheckupHistoryRows,
} from 'components/cattles/checkupPage/utils/checkupHistory'
import { morphForWords } from 'components/shared/processes'
import { useGetTreatmentSchemas } from 'hook/query/useGetTreatmentSchemas'
import { ICheckupList } from 'models'
import moment from 'moment'
import { useEffect, useState } from 'react'
import CattlesApiService from 'services/cattlesApiServices'
import { COLORS } from 'styles/colors'
import ExpandableCard from 'ui/expandable-card/ExpandableCard'
import CustomTableRow from 'ui/table/CustomTableRow'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'

type ViewHistoryBlockType = {
	cowId: number
}

const renderDash = () => (
	<TypographyPrimary>{EMPTY_HISTORY_CELL}</TypographyPrimary>
)

const ViewHistoryBlock = (props: ViewHistoryBlockType) => {
	const { cowId } = props
	const [examinatorsInfo, setExaminatorsInfo] = useState<ICheckupList[]>([])
	const { data: treatmentSchemas = [] } = useGetTreatmentSchemas()

	useEffect(() => {
		CattlesApiService.getExaminationCowInfoByCowId(cowId).then(data => {
			setExaminatorsInfo(data)
		})
	}, [cowId])

	const historyRows = buildCheckupHistoryRows(examinatorsInfo, treatmentSchemas)
	const transformedData = historyRows.map(row => ({
		rowKey: row.id,
		height: '100%',
		cellItems: [
			{
				width: '10%',
				padding: '16px 8px 16px 0px',
				children: (
					<TypographyPrimary>
						{row.createdDt
							? moment(row.createdDt).format('DD.MM.YYYY')
							: EMPTY_HISTORY_CELL}
					</TypographyPrimary>
				),
			},
			{
				width: '10%',
				padding: '16px 8px 16px 0px',
				children:
					row.limpness == null ? (
						renderDash()
					) : (
						<TypographyPrimary>
							{row.limpness}{' '}
							<span style={{ color: COLORS.secondaryFont }}>
								{morphForWords(row.limpness, ['балл', 'балла', 'баллов'])}
							</span>
						</TypographyPrimary>
					),
			},
			{
				width: '14%',
				padding: '16px 8px 16px 0px',
				children: (
					<TypographyPrimary
						sx={{
							color: row.event.isError ? COLORS.error : COLORS.black,
						}}
					>
						{row.event.label}
					</TypographyPrimary>
				),
			},
			{
				width: '24%',
				padding: '16px 8px 16px 0px',
				children: row.diagnosisHoof.length ? (
					<Box display='flex' flexDirection='column' gap='12px'>
						{row.diagnosisHoof.map(item => (
							<Box key={`${item.diagnosis}-${item.hooves}`}>
								<TypographyPrimary>{item.diagnosis}</TypographyPrimary>
								{item.hooves && (
									<TypographySecondary14>{item.hooves}</TypographySecondary14>
								)}
							</Box>
						))}
					</Box>
				) : (
					renderDash()
				),
			},
			{
				width: '26%',
				padding: '16px 8px 16px 0px',
				children: row.schemes.length ? (
					<Box display='flex' flexDirection='column' gap='12px'>
						{row.schemes.map((scheme, index) => (
							<Box key={`${scheme.title}-${index}`}>
								<TypographyPrimary>{scheme.title}</TypographyPrimary>
								{scheme.durationLabel && (
									<TypographySecondary14>
										{scheme.durationLabel}
									</TypographySecondary14>
								)}
							</Box>
						))}
					</Box>
				) : (
					renderDash()
				),
			},
			{
				width: '16%',
				padding: '16px 8px 16px 0px',
				children: (
					<TypographyPrimary>
						{row.performer || EMPTY_HISTORY_CELL}
					</TypographyPrimary>
				),
			},
		],
	}))

	return (
		<ExpandableCard
			title='История осмотров'
			titleSize={TypographySize.MEDIUM}
			isDefaultOpen={false}
		>
			<Box>
				<TableContainer
					sx={{
						overflowY: 'auto',
						overflowX: 'hidden',
						height: '267px',
						'&::-webkit-scrollbar': {
							width: 4,
						},
						'&::-webkit-scrollbar-track': {
							backgroundColor: '#DBDBDB',
							borderRadius: 12,
						},
						'&::-webkit-scrollbar-thumb': {
							backgroundColor: COLORS.icon,
							borderRadius: 12,
						},
					}}
				>
					<Table>
						<TableHead
							sx={{
								[`& .${tableCellClasses.root}`]: {
									backgroundColor: '#fff',
									position: 'sticky',
									top: 0,
									zIndex: 1,
								},
							}}
						>
							<CustomTableRow
								rowKey={tableHead.rowKey}
								cellItems={tableHead.cellItems}
								rowBorderBottom={tableHead.rowBorderBottom}
								rowHeight={tableHead.height}
							/>
						</TableHead>
						<TableBody>
							{transformedData.length === 0 ? (
								<TableRow
									sx={{
										width: '100%',
										[`& .${tableCellClasses.root}`]: {
											borderBottom: 'none',
											height: '200px',
										},
									}}
								>
									<TableCell colSpan={12} align={'center'} padding={'none'}>
										<TypographySecondary14>
											Хромоты и осмотров ранее не обнаружено
										</TypographySecondary14>
									</TableCell>
								</TableRow>
							) : (
								transformedData.map(el => (
									<CustomTableRow
										key={el.rowKey}
										rowKey={el.rowKey}
										cellItems={el.cellItems}
										rowHeight={el.height}
									/>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
				{historyRows.length > 0 && (
					<TypographySecondary>Всего: {historyRows.length}</TypographySecondary>
				)}
			</Box>
		</ExpandableCard>
	)
}

export default ViewHistoryBlock

const tableHead = {
	rowKey: 'tableHistoryViewHead',
	rowBorderBottom: COLORS.secondaryFontOpacity,
	height: '40px',
	cellItems: [
		{
			width: '10%',
			children: <TypographySecondary>Дата</TypographySecondary>,
		},
		{
			width: '10%',
			children: (
				<TypographySecondary>
					Балл
					<br />
					хромоты
				</TypographySecondary>
			),
		},
		{
			width: '14%',
			children: <TypographySecondary>Событие</TypographySecondary>,
		},
		{
			width: '24%',
			children: (
				<TypographySecondary>
					Диагноз /<br />
					Больное копыто
				</TypographySecondary>
			),
		},
		{
			width: '26%',
			children: <TypographySecondary>Схема лечения</TypographySecondary>,
		},
		{
			width: '16%',
			children: <TypographySecondary>Исполнитель</TypographySecondary>,
		},
	],
}
